import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Plus,
  Search,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  User,
  CheckCircle2,
  PenLine
} from 'lucide-react';

const Encomendas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [encomendas, setEncomendas] = useState([]);
  const [apartamentos, setApartamentos] = useState([]);
  const [porteiros, setPorteiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEncomenda, setEditingEncomenda] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [encomendasPagination, setEncomendasPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [filters, setFilters] = useState({
    status: 'aguardando', // Por padrão, mostrar apenas encomendas aguardando
    search: '',
    data_inicio: '',
    data_fim: '',
    codigo: '',
    apartamento_id: ''
  });
  const [formData, setFormData] = useState({
    encomenda_destinatario_nome: '',
    apartamento_id: '',
    encomenda_destinatario_telefone: '',
    encomenda_remetente_nome: '',
    encomenda_remetente_empresa: '',
    encomenda_remetente_telefone: '',
    encomenda_tipo: 'encomenda',
    encomenda_descricao: '',
    encomenda_peso: '',
    encomenda_altura: '',
    encomenda_largura: '',
    encomenda_profundidade: '',
    encomenda_valor: '',
    encomenda_observacoes: '',
    encomenda_status: 'aguardando',
    entregue_para: ''
  });
  const [previewEncomenda, setPreviewEncomenda] = useState(null);
  const [previewFotos, setPreviewFotos] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const apiRootUrl = apiBaseUrl.replace(/\/api\/?$/, '');

  const resolveRelativeAssetUrl = (path) => {
    if (!path) return null;
    return `${apiRootUrl}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const isLikelyBase64 = (value) => /^[A-Za-z0-9+/=]+$/.test(value) && value.length > 100;

  const parseEncomendaFotos = (rawFotos) => {
    if (!rawFotos) {
      return [];
    }

    let fotosBrutas = [];

    if (Array.isArray(rawFotos)) {
      fotosBrutas = rawFotos;
    } else if (typeof rawFotos === 'string') {
      const trimmed = rawFotos.trim();

      if (!trimmed) {
        return [];
      }

      const looksLikeJson =
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'));

      if (looksLikeJson) {
        try {
          const parsed = JSON.parse(trimmed);
          fotosBrutas = Array.isArray(parsed) ? parsed : [parsed];
        } catch (error) {
          console.error('Erro ao fazer parse de encomenda_fotos:', error);
          fotosBrutas = [trimmed];
        }
      } else {
        fotosBrutas = [trimmed];
      }
    } else if (typeof rawFotos === 'object') {
      fotosBrutas = [rawFotos];
    }

    const normalizarFoto = (foto) => {
      if (!foto) return null;

      if (typeof foto === 'string') {
        const valor = foto.trim();
        if (!valor) return null;
        if (/^data:image/i.test(valor) || /^https?:\/\//i.test(valor)) {
          return valor;
        }
        if (
          valor.startsWith('/uploads') ||
          valor.startsWith('uploads') ||
          valor.startsWith('files') ||
          valor.startsWith('/files')
        ) {
          return resolveRelativeAssetUrl(valor);
        }
        if (isLikelyBase64(valor)) {
          return `data:image/jpeg;base64,${valor}`;
        }
        return valor;
      }

      if (typeof foto === 'object') {
        const candidato = foto.uri || foto.url || foto.data || foto.base64 || foto.path;
        if (!candidato) return null;
        if (/^data:image/i.test(candidato) || /^https?:\/\//i.test(candidato)) {
          return candidato;
        }
        if (
          candidato.startsWith('/uploads') ||
          candidato.startsWith('uploads') ||
          candidato.startsWith('files') ||
          candidato.startsWith('/files')
        ) {
          return resolveRelativeAssetUrl(candidato);
        }
        if (isLikelyBase64(candidato)) {
          return `data:image/jpeg;base64,${candidato}`;
        }
        return candidato;
      }

      return null;
    };

    return fotosBrutas.map(normalizarFoto).filter(Boolean);
  };

  const handlePreviewFotos = (encomenda) => {
    const fotos = parseEncomendaFotos(encomenda.encomenda_fotos);
    if (!fotos.length) {
      window.alert('Nenhuma foto disponível para esta encomenda.');
      return;
    }
    setPreviewEncomenda(encomenda);
    setPreviewFotos(fotos);
    setShowPreviewModal(true);
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewEncomenda(null);
    setPreviewFotos([]);
  };

  const normalizeAssinatura = (assinatura) => {
    if (!assinatura) return null;
    const valor = assinatura.trim();
    if (!valor) return null;
    if (/^data:image/i.test(valor) || /^https?:\/\//i.test(valor)) {
      return valor;
    }
    if (
      valor.startsWith('/uploads') ||
      valor.startsWith('uploads') ||
      valor.startsWith('files') ||
      valor.startsWith('/files')
    ) {
      return resolveRelativeAssetUrl(valor);
    }
    if (isLikelyBase64(valor)) {
      return `data:image/png;base64,${valor}`;
    }
    return null;
  };

  const handlePreviewAssinatura = (encomenda) => {
    const assinaturaUrl = normalizeAssinatura(encomenda.encomenda_assinatura);
    if (!assinaturaUrl) {
      window.alert('Nenhuma assinatura disponível para esta encomenda.');
      return;
    }
    setPreviewEncomenda(encomenda);
    setSignaturePreview(assinaturaUrl);
    setShowSignatureModal(true);
  };

  const closeSignatureModal = () => {
    setShowSignatureModal(false);
    setPreviewEncomenda(null);
    setSignaturePreview(null);
  };

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      loadApartamentos();
      loadPorteiros();
    }
  }, [condominioSelecionado]);

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) loadEncomendas();
  }, [condominioSelecionado, encomendasPagination.page, filters.status, filters.apartamento_id, filters.data_inicio, filters.data_fim, filters.codigo]);

  const loadEncomendas = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) return;

      const params = new URLSearchParams();
      params.append('condominio_id', condominioId);
      params.append('page', encomendasPagination.page);
      params.append('limit', encomendasPagination.limit);

      // Se for morador, filtrar apenas pelo apartamento do usuário
      if (!isAdministrador() && user?.vinculos) {
        const vinculoAtivo = user.vinculos.find(v => v.condominio_id === condominioId);
        if (vinculoAtivo?.apartamento_id) {
          params.append('apartamento_id', vinculoAtivo.apartamento_id);
        }
      } else {
        // Administrador pode filtrar por qualquer apartamento
        if (filters.apartamento_id) params.append('apartamento_id', filters.apartamento_id);
      }
      
      if (filters.status) params.append('status', filters.status);
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.codigo) params.append('codigo', filters.codigo);

      const response = await api.get(`/encomendas?${params.toString()}`);
      const data = response.data.data;
      setEncomendas(data.encomendas || []);
      const pag = data.pagination || {};
      setEncomendasPagination(prev => ({ ...prev, total: pag.total || 0, pages: Math.max(1, pag.pages || 1) }));
    } catch (error) {
      console.error('Erro ao carregar encomendas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApartamentos = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) return;

      const response = await api.get(`/apartamentos?condominio_id=${condominioId}`);
      setApartamentos(response.data.data.apartamentos);
    } catch (error) {
      console.error('Erro ao carregar apartamentos:', error);
    }
  };

  const loadPorteiros = async () => {
    try {
      const response = await api.get('/encomendas/porteiros');
      setPorteiros(response.data.data.porteiros);
    } catch (error) {
      console.error('Erro ao carregar porteiros:', error);
    }
  };

  // Função para aplicar máscara de telefone
  const applyPhoneMask = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara (11) 99999-9999
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Aplicar máscara de telefone
    if (name === 'encomenda_destinatario_telefone' || name === 'encomenda_remetente_telefone') {
      processedValue = applyPhoneMask(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const dadosParaEnvio = {
        ...formData,
        condominio_id: getCondominioId()
      };

      // Na criação, sempre definir status como 'aguardando' e limpar 'entregue_para'
      if (!editingEncomenda) {
        dadosParaEnvio.encomenda_status = 'aguardando';
        dadosParaEnvio.entregue_para = '';
      }

      if (editingEncomenda) {
        await api.put(`/encomendas/${editingEncomenda.encomenda_id}`, dadosParaEnvio);
      } else {
        await api.post('/encomendas', dadosParaEnvio);
      }

      setShowModal(false);
      setEditingEncomenda(null);
      resetForm();
      loadEncomendas();
    } catch (error) {
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else {
        setErrorMessage('Erro ao salvar encomenda');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (encomenda) => {
    setEditingEncomenda(encomenda);
    setFormData({
      encomenda_destinatario_nome: encomenda.encomenda_destinatario_nome || '',
      apartamento_id: encomenda.apartamento_id || '',
      encomenda_destinatario_telefone: encomenda.encomenda_destinatario_telefone || '',
      encomenda_remetente_nome: encomenda.encomenda_remetente_nome || '',
      encomenda_remetente_empresa: encomenda.encomenda_remetente_empresa || '',
      encomenda_remetente_telefone: encomenda.encomenda_remetente_telefone || '',
      encomenda_tipo: encomenda.encomenda_tipo || 'encomenda',
      encomenda_descricao: encomenda.encomenda_descricao || '',
      encomenda_peso: encomenda.encomenda_peso || '',
      encomenda_altura: encomenda.encomenda_altura || '',
      encomenda_largura: encomenda.encomenda_largura || '',
      encomenda_profundidade: encomenda.encomenda_profundidade || '',
      encomenda_valor: encomenda.encomenda_valor || '',
      encomenda_observacoes: encomenda.encomenda_observacoes || '',
      encomenda_status: encomenda.encomenda_status || 'aguardando',
      entregue_para: encomenda.entregue_para || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta encomenda?')) {
      try {
        await api.delete(`/encomendas/${id}`);
        loadEncomendas();
      } catch (error) {
        console.error('Erro ao excluir encomenda:', error);
        alert('Erro ao excluir encomenda');
      }
    }
  };

  const handleConfirmarEntrega = (encomenda) => {
    navigate(`/encomendas/${encomenda.encomenda_id}/confirmar-entrega`);
  };

  const resetForm = () => {
    setFormData({
      encomenda_destinatario_nome: '',
      apartamento_id: '',
      encomenda_destinatario_telefone: '',
      encomenda_remetente_nome: '',
      encomenda_remetente_empresa: '',
      encomenda_remetente_telefone: '',
      encomenda_tipo: 'encomenda',
      encomenda_descricao: '',
      encomenda_peso: '',
      encomenda_altura: '',
      encomenda_largura: '',
      encomenda_profundidade: '',
      encomenda_valor: '',
      encomenda_observacoes: '',
      encomenda_status: 'aguardando',
      entregue_para: ''
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aguardando':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'entregue':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'devolvida':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'perdida':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      default:
        return <Package className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'aguardando':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'entregue':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'devolvida':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'perdida':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Encomendas</h1>
        {(['administrador', 'sindico', 'subsindico'].includes(user?.tipo)) && (
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nova Encomenda
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="input"
            >
              <option value="">Todos</option>
              <option value="aguardando">Aguardando</option>
              <option value="entregue">Entregue</option>
              <option value="devolvida">Devolvida</option>
              <option value="perdida">Perdida</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
            <input
              type="text"
              placeholder="Ex: ENC-20251002-001"
              value={filters.codigo}
              onChange={(e) => setFilters(prev => ({ ...prev, codigo: e.target.value }))}
              className="input"
            />
          </div>
          {isAdministrador() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apartamento</label>
              <select
                value={filters.apartamento_id}
                onChange={(e) => setFilters(prev => ({ ...prev, apartamento_id: e.target.value }))}
                className="input"
              >
                <option value="">Todos</option>
                {apartamentos.map(apt => (
                  <option key={apt.apartamento_id} value={apt.apartamento_id}>
                    {apt.apartamento_bloco} - {apt.apartamento_numero}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
            <input
              type="date"
              value={filters.data_inicio}
              onChange={(e) => setFilters(prev => ({ ...prev, data_inicio: e.target.value }))}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
            <input
              type="date"
              value={filters.data_fim}
              onChange={(e) => setFilters(prev => ({ ...prev, data_fim: e.target.value }))}
              className="input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={loadEncomendas}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Filtrar
            </button>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => setFilters({
              status: '',
              search: '',
              data_inicio: '',
              data_fim: '',
              codigo: '',
              apartamento_id: ''
            })}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 text-sm"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Destinatário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Apartamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remetente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Foto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recebido em
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recebido por
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entregue em
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entregue para
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {encomendas.map((encomenda) => {
                const fotos = parseEncomendaFotos(encomenda.encomenda_fotos);
                return (
                  <tr key={encomenda.encomenda_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {encomenda.encomenda_codigo}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{encomenda.encomenda_destinatario_nome}</div>
                    {encomenda.encomenda_destinatario_telefone && (
                      <div className="text-sm text-gray-500">{encomenda.encomenda_destinatario_telefone}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {encomenda.apartamento?.apartamento_bloco} - {encomenda.apartamento?.apartamento_numero}
                    </div>
                    <div className="text-sm text-gray-500">
                      {encomenda.apartamento?.apartamento_andar}º andar
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{encomenda.encomenda_remetente_nome}</div>
                    {encomenda.encomenda_remetente_empresa && (
                      <div className="text-sm text-gray-500">{encomenda.encomenda_remetente_empresa}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {encomenda.encomenda_tipo}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {fotos.length > 0 ? (
                      <img
                        src={fotos[0]}
                        alt={`Foto da encomenda ${encomenda.encomenda_codigo}`}
                        className="w-16 h-16 object-cover rounded border border-gray-200"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">Sem foto</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(encomenda.encomenda_status)}
                      <span className={getStatusBadge(encomenda.encomenda_status)}>
                        {encomenda.encomenda_status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateTime(encomenda.encomenda_data_recebimento)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {encomenda.recebidoPor?.usuario_nome || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateTime(encomenda.encomenda_data_entrega)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {encomenda.entregue_para || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      {encomenda.encomenda_status === 'aguardando' && !['morador', 'usuario'].includes(user?.tipo) && (
                        <button
                          onClick={() => handleConfirmarEntrega(encomenda)}
                          className="text-green-600 hover:text-green-900"
                          title="Confirmar Entrega"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      {(['administrador', 'sindico', 'subsindico'].includes(user?.tipo)) && (
                        <>
                          {fotos.length > 0 && (
                            <button
                              onClick={() => handlePreviewFotos(encomenda)}
                              className="text-gray-600 hover:text-gray-900"
                              title="Visualizar Fotos"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {normalizeAssinatura(encomenda.encomenda_assinatura) && (
                            <button
                              onClick={() => handlePreviewAssinatura(encomenda)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Visualizar Assinatura"
                            >
                              <PenLine className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(encomenda)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(encomenda.encomenda_id)}
                            className="text-red-600 hover:text-red-900"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto encomendas-modal">
            <h2 className="text-xl font-bold mb-4">
              {editingEncomenda ? 'Editar Encomenda' : 'Nova Encomenda'}
            </h2>

            {errorMessage && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Bloco Destinatário */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Destinatário
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome *
                    </label>
                    <input
                      type="text"
                      name="encomenda_destinatario_nome"
                      value={formData.encomenda_destinatario_nome}
                      onChange={handleInputChange}
                      required
                      maxLength={100}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      name="encomenda_destinatario_telefone"
                      value={formData.encomenda_destinatario_telefone}
                      onChange={handleInputChange}
                      maxLength={15}
                      placeholder="(11) 99999-9999"
                      className="input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apartamento *
                    </label>
                    <select
                      name="apartamento_id"
                      value={formData.apartamento_id}
                      onChange={handleInputChange}
                      required
                      className="input"
                    >
                      <option value="">Selecione o apartamento</option>
                      {apartamentos.map((apt) => (
                        <option key={apt.apartamento_id} value={apt.apartamento_id}>
                          {apt.apartamento_bloco} - {apt.apartamento_numero} ({apt.apartamento_andar}º andar)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Bloco Remetente */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Remetente
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome *
                    </label>
                    <input
                      type="text"
                      name="encomenda_remetente_nome"
                      value={formData.encomenda_remetente_nome}
                      onChange={handleInputChange}
                      required
                      maxLength={100}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      name="encomenda_remetente_telefone"
                      value={formData.encomenda_remetente_telefone}
                      onChange={handleInputChange}
                      maxLength={15}
                      placeholder="(11) 99999-9999"
                      className="input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Empresa
                    </label>
                    <input
                      type="text"
                      name="encomenda_remetente_empresa"
                      value={formData.encomenda_remetente_empresa}
                      onChange={handleInputChange}
                      maxLength={100}
                      className="input"
                    />
                  </div>
                </div>
              </div>

              {/* Bloco Recebido Por */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Recebido Por
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recebido por
                    </label>
                    <input
                      type="text"
                      value={user?.nome || 'Usuário logado'}
                      disabled
                      className="input bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Usuário logado (automático)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Recebimento
                    </label>
                    <input
                      type="datetime-local"
                      value={new Date().toISOString().slice(0, 16)}
                      disabled
                      className="input bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Data atual (automática)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Entrega
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.encomenda_status === 'entregue' ? new Date().toISOString().slice(0, 16) : ''}
                      disabled
                      className="input bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.encomenda_status === 'entregue' ? 'Atualizada automaticamente' : 'Atualiza quando status for "Entregue"'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bloco Informações da Encomenda */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Informações da Encomenda
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Encomenda
                    </label>
                    <select
                      name="encomenda_tipo"
                      value={formData.encomenda_tipo}
                      onChange={handleInputChange}
                      className="input"
                    >
                      <option value="encomenda">Encomenda</option>
                      <option value="correspondencia">Correspondência</option>
                      <option value="documento">Documento</option>
                      <option value="medicamento">Medicamento</option>
                      <option value="alimento">Alimento</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="encomenda_status"
                      value={formData.encomenda_status}
                      onChange={handleInputChange}
                      disabled={!editingEncomenda}
                      className={`input ${!editingEncomenda ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      title={!editingEncomenda ? 'Status não pode ser alterado na criação. Use o botão "Confirmar Entrega" para marcar como entregue.' : ''}
                    >
                      <option value="aguardando">Aguardando</option>
                      <option value="entregue">Entregue</option>
                      <option value="devolvida">Devolvida</option>
                      <option value="perdida">Perdida</option>
                    </select>
                    {!editingEncomenda && (
                      <p className="text-xs text-gray-500 mt-1">
                        Status será "Aguardando" por padrão. Use o botão "Confirmar Entrega" para marcar como entregue.
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entregue para
                    </label>
                    <input
                      type="text"
                      name="entregue_para"
                      value={formData.entregue_para}
                      onChange={handleInputChange}
                      placeholder="Nome da pessoa que recebeu a encomenda"
                      maxLength={100}
                      disabled={!editingEncomenda}
                      className={`input ${!editingEncomenda ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      title={!editingEncomenda ? 'Este campo será preenchido automaticamente ao confirmar a entrega.' : ''}
                    />
                    {!editingEncomenda && (
                      <p className="text-xs text-gray-500 mt-1">
                        Este campo será preenchido ao confirmar a entrega.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <textarea
                  name="encomenda_descricao"
                  value={formData.encomenda_descricao}
                  onChange={handleInputChange}
                  required
                  maxLength={200}
                  rows={3}
                  className="input"
                />
              </div>

              {/* Dimensões e Peso */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peso (kg)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="encomenda_peso"
                    value={formData.encomenda_peso}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Altura (cm)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="encomenda_altura"
                    value={formData.encomenda_altura}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Largura (cm)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="encomenda_largura"
                    value={formData.encomenda_largura}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profundidade (cm)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="encomenda_profundidade"
                    value={formData.encomenda_profundidade}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="encomenda_valor"
                  value={formData.encomenda_valor}
                  onChange={handleInputChange}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  name="encomenda_observacoes"
                  value={formData.encomenda_observacoes}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={3}
                  className="input"
                />
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEncomenda(null);
                    resetForm();
                    setErrorMessage('');
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Fotos da Encomenda {previewEncomenda?.encomenda_codigo || ''}
              </h2>
              <button
                onClick={closePreviewModal}
                className="text-gray-500 hover:text-gray-800"
              >
                Fechar
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {previewEncomenda && (
                <div className="text-sm text-gray-600">
                  <p>
                    <strong>Destinatário:</strong> {previewEncomenda.encomenda_destinatario_nome}
                  </p>
                  <p>
                    <strong>Apartamento:</strong>{' '}
                    {previewEncomenda.apartamento?.apartamento_bloco} - {previewEncomenda.apartamento?.apartamento_numero}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {previewFotos.map((foto, index) => (
                  <div
                    key={`${foto}-${index}`}
                    className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                  >
                    <img
                      src={foto}
                      alt={`Foto ${index + 1} da encomenda`}
                      className="w-full h-64 object-contain bg-white"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closePreviewModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Assinatura da Encomenda {previewEncomenda?.encomenda_codigo || ''}
              </h2>
              <button
                onClick={closeSignatureModal}
                className="text-gray-500 hover:text-gray-800"
              >
                Fechar
              </button>
            </div>
            <div className="px-6 py-6 flex justify-center bg-gray-50">
              {signaturePreview ? (
                <img
                  src={signaturePreview}
                  alt="Assinatura do destinatário"
                  className="max-h-[70vh] object-contain bg-white rounded border border-gray-200 p-4"
                />
              ) : (
                <span className="text-gray-500 text-sm">Assinatura não disponível.</span>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeSignatureModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Encomendas;

// Estilos CSS personalizados para o modal de Encomendas
const encomendasStyles = `
  .encomendas-modal .input-field {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s ease-in-out;
    background-color: #f9fafb;
  }
  
  .encomendas-modal .input-field:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  .encomendas-modal .input-field:hover {
    border-color: #d1d5db;
    background-color: #ffffff;
  }
  
  .encomendas-modal .btn-primary {
    background-color: #3b82f6;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .encomendas-modal .btn-primary:hover:not(:disabled) {
    background-color: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  .encomendas-modal .btn-primary:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
  
  .encomendas-modal .btn-secondary {
    background-color: #6b7280;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .encomendas-modal .btn-secondary:hover {
    background-color: #4b5563;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
  }
`;

// Adicionar estilos ao documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = encomendasStyles;
  document.head.appendChild(styleSheet);
}